import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Button } from "@/components/ui/button";
import { COMPARISONS } from "@/data/comparisons";
import { brandedTitle, safeDesc } from "@/lib/seo-meta";

const SITE = "https://heallexa.com";

const META_TITLE = brandedTitle("Heallexa vs Zocdoc & Top Healthcare Directories");
const META_DESC = safeDesc(
  "Compare Heallexa with Zocdoc, Healthgrades, WebMD, Vitals, Sesame, and more. Find the best healthcare directory for patients and providers in the U.S."
);

export const Route = createFileRoute("/compare/")({
 head: () => ({
 meta: [
 { title: META_TITLE },
 { name: "description", content: META_DESC },
 { property: "og:title", content: META_TITLE },
 { property: "og:description", content: META_DESC },
 { property: "og:url", content: `${SITE}/compare` },
 { property: "og:type", content: "website" },
 { name: "twitter:title", content: META_TITLE },
 { name: "twitter:description", content: META_DESC },
 ],
 links: [{ rel: "canonical", href: `${SITE}/compare` }],
 scripts: [
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "FAQPage",
 mainEntity: [
 {
 "@type": "Question",
 name: "What is Heallexa?",
 acceptedAnswer: {
 "@type": "Answer",
 text: "Heallexa is an AI-powered healthcare directory that helps patients find doctors, dentists, therapists, and other providers by specialty, insurance, location, and availability. It is free for patients and offers free and premium listing plans for providers.",
 },
 },
 {
 "@type": "Question",
 name: "How does Heallexa compare to Zocdoc?",
 acceptedAnswer: {
 "@type": "Answer",
 text: "Heallexa offers AI-powered provider discovery, claimable listings, and appointment requests. Unlike Zocdoc, Heallexa has a free listing tier and covers all healthcare specialties, not just those inside a booking network.",
 },
 },
 {
 "@type": "Question",
 name: "Is Heallexa free for patients?",
 acceptedAnswer: {
 "@type": "Answer",
 text: "Yes. Searching, browsing, and viewing provider details are completely free for patients on Heallexa. No account is required to search.",
 },
 },
 {
 "@type": "Question",
 name: "Is Heallexa free for providers?",
 acceptedAnswer: {
 "@type": "Answer",
 text: "Heallexa offers a free listing tier with a public profile, phone, address, and website. Premium plans with additional visibility and features start at $99 per month.",
 },
 },
 ],
 }),
 },
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "BreadcrumbList",
 itemListElement: [
 { "@type": "ListItem", position: 1, name: "Home", item: SITE },
 { "@type": "ListItem", position: 2, name: "Compare", item: `${SITE}/compare` },
 ],
 }),
 },
 ],
 }),
 component: CompareHubPage,
});

function CompareHubPage() {
 return (
 <div className="min-h-screen bg-background pb-24 md:pb-1">
 <Navbar />

 <header className="pt-28 pb-14 border-b border-border bg-card">
 <div className="mx-auto max-w-4xl px-6 text-center">
 <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-3">Compare healthcare directories</p>
 <h1 className="text-4xl md:text-6xl font-semibold tracking-tight text-foreground" style={{ letterSpacing: "-0.02em" }}>
 Compare Heallexa With Other Healthcare Directories
 </h1>
 <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto">
 See how Heallexa compares with popular doctor booking platforms, healthcare directories, review sites, and provider listing platforms. Heallexa helps patients find care faster while giving providers a simple way to claim, manage, and grow their online presence.
 </p>
 <div className="mt-8 flex flex-wrap justify-center gap-3">
 <Button asChild size="lg"><Link to="/search">Find a Provider</Link></Button>
 <Button asChild size="lg" variant="outline"><Link to="/list-your-practice">List Your Practice</Link></Button>
 <Button asChild size="lg" variant="outline"><Link to="/browse">Claim Your Listing</Link></Button>
 </div>
 </div>
 </header>

 <main className="mx-auto max-w-6xl px-6 py-14">
 <h2 className="text-2xl md:text-3xl font-semibold mb-2">Side-by-side comparisons</h2>
 <p className="text-muted-foreground mb-8">Pick a platform to see how it compares to Heallexa.</p>

 <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
 {COMPARISONS.map((c) => (
 <li key={c.slug}>
 <Link
 to="/compare/$slug"
 params={{ slug: c.slug }}
 className="group block h-full rounded-xl border border-border bg-card p-5 transition hover:border-primary/40 hover:shadow-md"
 >
 <p className="text-xs font-medium text-primary uppercase tracking-wider">Comparison</p>
 <h3 className="mt-1 text-lg font-semibold text-foreground">Heallexa vs {c.competitor}</h3>
 <p className="mt-2 text-sm text-muted-foreground line-clamp-3">
 {c.primaryKeyword}, see how Heallexa stacks up for patients and providers.
 </p>
 <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
 Read comparison <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
 </span>
 </Link>
 </li>
 ))}
 </ul>

 <section className="mt-16 rounded-2xl border border-border bg-gradient-to-br from-primary/5 to-card p-8 text-center">
 <h2 className="text-2xl md:text-3xl font-semibold">Ready to Find Care or Grow Your Practice?</h2>
 <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
 Whether you are a patient looking for the right provider or a healthcare practice looking for more visibility, Heallexa gives you a simple way to connect.
 </p>
 <div className="mt-6 flex flex-wrap justify-center gap-3">
 <Button asChild size="lg"><Link to="/search">Find a Provider</Link></Button>
 <Button asChild size="lg" variant="outline"><Link to="/browse">Claim Your Listing</Link></Button>
 <Button asChild size="lg" variant="outline"><Link to="/list-your-practice">List Your Practice</Link></Button>
 </div>
 </section>

 <p className="mt-10 text-xs text-muted-foreground text-center">
 Comparison information is for general educational purposes and may change. Please visit each platform directly for the most current details.
 </p>
 </main>

 <FooterSection />

 <div className="fixed bottom-0 inset-x-0 z-40 md:hidden border-t border-border bg-card/95 backdrop-blur p-3 flex gap-2">
 <Button asChild className="flex-1"><Link to="/search">Find Provider</Link></Button>
 <Button asChild variant="outline" className="flex-1"><Link to="/list-your-practice">List Practice</Link></Button>
 </div>
 </div>
 );
}
